"use client";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-semibold transition-[color,background-color,border-color,transform] duration-150 ease active:scale-97 focus-visible:outline-hidden focus-visible:ring focus-visible:ring-primary/75 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-muted text-foreground hover:bg-button-hover",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border-accent-foreground text-accent-foreground border bg-transparent hover:bg-primary-foreground hover:text-accent-foreground",
        primary: "bg-accent text-card hover:bg-accent/90",
        secondary: "bg-accent text-foreground hover:bg-accent/80",
        ghost: "bg-muted text-foreground hover:bg-button-hover",
        link: "text-accent-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 rounded-lg px-4 py-2 text-sm",
        xs: "px-2 py-1 rounded text-xs font-medium",
        sm: "h-11 rounded-lg px-4 text-sm",
        lg: "h-[52px] rounded-lg px-4 py-3.5 text-base",
        icon: "h-9 w-9 rounded-full [&_svg]:size-4",
        full: "w-full rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      loading,
      children,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading === true || disabled === true}
        {...props}
      >
        {loading === true && <LoaderIcon className="h-4 w-4 animate-spin" />}
        {children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
